import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../ui/loading';
import { Calendar, ChevronLeft, X, GraduationCap } from 'lucide-react';
import { useDashboardAnalytics } from '../../hooks/useDashboardAnalytics';
import { useMyClasses, useClassAllocation } from '../../hooks/useClassAllocation';
import { CourseCard } from './Dashboard';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFullDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function OccupancyBar({ current, max }) {
  const pct = max > 0 ? (current / max) * 100 : 0;
  const isOver = current > max;
  const isFull = !isOver && pct >= 100;
  const isInProgress = !isOver && !isFull && pct > 0;
  return (
    <div className="h-1.5 rounded-full bg-muted flex-1">
      <div
        className={`h-1.5 rounded-full transition-all ${isOver ? 'bg-red-500' : isFull ? 'bg-green-500' : ''}`}
        style={{
          width: `${Math.min(pct, 100)}%`,
          ...(isInProgress ? { background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' } : {}),
        }}
      />
    </div>
  );
}

function yearBadgeClass(yearOrder) {
  switch (yearOrder) {
    case 1: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
    case 2: return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    case 3: return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
    case 4: return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300';
    case 5: return 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300';
    case 6: return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
    default: return 'bg-muted text-muted-foreground';
  }
}

// ─── Read-only class card ─────────────────────────────────────────────────────

function ReadOnlyClassCard({ cls, isOwn, isSelected, onSelect }) {
  const { current_enrollment, capacity } = cls;

  const cardClass = isSelected
    ? 'border-primary ring-1 ring-primary bg-primary/5'
    : isOwn
    ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-700 hover:shadow-sm'
    : 'bg-card border-border hover:shadow-sm';

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-lg border p-3 transition-all ${cardClass}`}
    >
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="font-semibold text-sm truncate flex items-center gap-1">
          {isOwn && <GraduationCap className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
          {cls.name}
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {current_enrollment}/{capacity}
        </span>
      </div>

      <OccupancyBar current={current_enrollment} max={capacity} />

      <div className="flex flex-wrap gap-1 mt-2">
        {cls.school_year_breakdown?.map((yb) => (
          <span
            key={yb.year}
            className={`text-xs px-1.5 py-0.5 rounded ${yearBadgeClass(yb.order)}`}
          >
            {yb.year}: {yb.count}
          </span>
        ))}
      </div>

      <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-0.5">
          <span className="inline-block w-2 h-2 rounded-full bg-pink-400" />
          {cls.gender_breakdown?.female ?? 0}F
        </span>
        <span className="flex items-center gap-0.5">
          <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
          {cls.gender_breakdown?.male ?? 0}M
        </span>
      </div>

      {cls.teacher_name && (
        <div className="mt-1 text-xs text-muted-foreground truncate">{cls.teacher_name}</div>
      )}
    </button>
  );
}

// ─── Student roster panel ─────────────────────────────────────────────────────

function StudentRosterPanel({ cls, students, onClose }) {
  const { t } = useTranslation();
  const sorted = [...students].sort((a, b) => a.last_name.localeCompare(b.last_name));

  return (
    <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden">
      <div className="p-3 border-b flex items-center justify-between flex-shrink-0">
        <div>
          <div className="font-semibold text-sm">{cls.name}</div>
          <div className="text-xs text-muted-foreground">
            {cls.current_enrollment}/{cls.capacity} {t('dashboard.students')}
          </div>
        </div>
        <button onClick={onClose} className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-muted">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {sorted.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">
            {t('teacherDashboard.noStudentsInClass')}
          </p>
        ) : (
          sorted.map((s, idx) => (
            <div
              key={s.uuid}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50"
            >
              <span className="text-xs text-muted-foreground w-5 text-right tabular-nums flex-shrink-0">
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <span className="font-medium">{s.last_name}, {s.first_name}</span>
              </div>
              {s.school_year && (
                <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${yearBadgeClass(s.school_year_order ?? 0)}`}>
                  {s.school_year}
                </span>
              )}
              <span
                className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                  s.gender === 'M' ? 'bg-blue-400' : s.gender === 'F' ? 'bg-pink-400' : 'bg-gray-400'
                }`}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Course detail — all classes, teacher's own highlighted ───────────────────

function TeacherCourseDetail({ course, myClassUuids, onBack }) {
  const { t } = useTranslation();
  const [selectedClass, setSelectedClass] = useState(null);

  const { classes, students: allStudents, isLoading } = useClassAllocation(course.uuid);

  const rosterStudents = selectedClass
    ? allStudents.filter((s) => s.class_group_uuid === selectedClass.uuid)
    : [];

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1 py-2 text-sm text-muted-foreground hover:text-foreground mb-2 -ml-1 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('dashboard.allCourses')}
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>
          {course.name}
        </h1>
        <div className="flex items-center gap-2 mt-1 text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{formatFullDate(course.start_date)} – {formatFullDate(course.end_date)}</span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="md" />
        </div>
      ) : classes.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-center text-muted-foreground">
          <GraduationCap className="h-10 w-10 mb-3 opacity-30" />
          <p className="font-medium">{t('teacherDashboard.noClassesForCourse')}</p>
        </div>
      ) : (
        <div className="md:flex md:gap-4 md:items-start space-y-4 md:space-y-0">
          {/* Class cards grid — hidden on mobile when roster is open */}
          <div className={`flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ${selectedClass ? 'hidden md:grid' : ''}`}>
            {classes.map((cls) => (
              <ReadOnlyClassCard
                key={cls.uuid}
                cls={cls}
                isOwn={myClassUuids.has(cls.uuid)}
                isSelected={selectedClass?.uuid === cls.uuid}
                onSelect={() => setSelectedClass(
                  selectedClass?.uuid === cls.uuid ? null : cls
                )}
              />
            ))}
          </div>

          {/* Student roster panel — full width on mobile, fixed sidebar on desktop */}
          {selectedClass && (
            <div className="w-full md:w-64 md:flex-shrink-0 fade-in">
              <StudentRosterPanel
                cls={selectedClass}
                students={rosterStudents}
                onClose={() => setSelectedClass(null)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main TeacherDashboard ────────────────────────────────────────────────────

export function TeacherDashboard() {
  const { t } = useTranslation();
  const [selectedCourse, setSelectedCourse] = useState(null);

  const { data: analytics, isLoading, error } = useDashboardAnalytics();
  const { data: myClasses } = useMyClasses();

  const myClassUuids = new Set((myClasses || []).map((c) => c.uuid));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-destructive">{t('dashboard.error', 'Error loading dashboard data')}</p>
          <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  const { courses } = analytics || {};
  const selectedCourseData = courses?.find((c) => c.uuid === selectedCourse);
  const otherCourses = courses?.filter((c) => c.uuid !== selectedCourse) || [];

  const n = courses?.length || 0;
  const cols =
    n <= 2 ? 2 :
    n === 3 ? 3 :
    n === 4 ? 2 :
    n <= 6 ? 3 : 4;
  const gridClass =
    cols === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-3xl' :
    cols === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl' :
                 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-7xl';

  return (
    <div className="pb-8">
      {selectedCourse && selectedCourseData ? (
        <div className="flex flex-col md:flex-row gap-5 items-start">
          <div key={selectedCourse} className="flex-1 min-w-0 fade-in">
            <TeacherCourseDetail
              course={selectedCourseData}
              myClassUuids={myClassUuids}
              onBack={() => setSelectedCourse(null)}
            />
          </div>
          {otherCourses.length > 0 && (
            <div className="hidden md:flex md:flex-col w-72 flex-shrink-0 space-y-3 mt-14 fade-in">
              {otherCourses.map((course) => (
                <CourseCard
                  key={course.uuid}
                  course={course}
                  onSelect={() => setSelectedCourse(course.uuid)}
                  showIndicator={false}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="fade-in">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>
              {t('dashboard.courseOverview')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
          </div>

          {courses?.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              {t('dashboard.noCourses')}
            </div>
          ) : (
            <div className={`grid gap-5 mx-auto w-full ${gridClass}`}>
              {courses?.map((course) => (
                <CourseCard
                  key={course.uuid}
                  course={course}
                  onSelect={() => setSelectedCourse(course.uuid)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TeacherDashboard;
